import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { JsonLd } from '@/components/JsonLd';
import { PageMeta, SITE_URL } from '@/components/PageMeta';
import { LeafletMap } from '@/components/map/mapLeaflet.tsx';
import { PastCard, Skeleton, UpcomingCard } from '@/components/organization-profile/cards';
import { ContactModal } from '@/components/ui/ContactModal';
import { ShareBloc } from '@/components/ui/ShareBloc';
import { ROUTES } from '@/config/routes';
import { type ArticleListItem, getPublishedArticlesApi } from '@/lib/api/blogApi';
import { getCompetitionsApi } from '@/lib/api/competitionsApi';
import { createOrgAdminRequestApi, getOrganizationApi, sendOrgMessageApi } from '@/lib/api/organizationsApi';
import { getFileUrl } from '@/lib/file-url';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, getNameInitials } from '@shared';
import { CompetitionStatus } from '@shared/enums/domain.enum';
import type { Competition, Organization } from '@shared/types/entities';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Award,
  Building2,
  Calendar,
  EyeOff,
  FileText,
  Globe,
  Mail,
  Map,
  MapPin,
  Phone,
  Trophy,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = ['Présentation', 'Concours', 'Événements passés', 'Blog', 'Contact'] as const;

const LOGO_COLORS = [
  'from-blue-700 to-indigo-600',
  'from-indigo-700 to-violet-600',
  'from-violet-700 to-purple-600',
  'from-cyan-700 to-blue-600',
  'from-emerald-700 to-teal-600',
  'from-purple-700 to-indigo-600',
];

// ─── Profile content ───────────────────────────────────────────
function ProfileContent({
  org,
  upcoming,
  upcomingTotal,
  past,
  pastTotal,
  articles,
}: {
  org: Organization;
  upcoming: Competition[];
  upcomingTotal: number;
  past: Competition[];
  pastTotal: number;
  articles: ArticleListItem[];
}) {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [adminMessage, setAdminMessage] = useState('');
  const [adminRequested, setAdminRequested] = useState(false);
  const { user, isAuthenticated } = useAuthStore();

  const contactMutation = useMutation({
    mutationFn: (data: { fullName: string; message: string; hcaptchaToken: string }) => sendOrgMessageApi(org.id, data),
    onSuccess: () => {
      toast.success('Message envoyé');
      setContactOpen(false);
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Erreur lors de l'envoi");
    },
  });

  const isAlreadyOrgAdmin =
    isAuthenticated && (user?.organizationsAdmin?.some(o => o.organizationId === org.id) ?? false);

  const adminRequestMutation = useMutation({
    mutationFn: () => createOrgAdminRequestApi(org.id, { message: adminMessage.trim() || undefined }),
    onSuccess: () => {
      setAdminRequested(true);
      setAdminMessage('');
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Erreur lors de la demande');
    },
  });

  const logoFile = org.files?.find(f => f.usage === 'ORG_LOGO') ?? null;
  const coverFile = org.files?.find(f => f.usage === 'ORG_COVER') ?? null;
  const logoColor = LOGO_COLORS[org.id % LOGO_COLORS.length];
  const totalCompetitions = upcomingTotal + pastTotal;
  const clubsCount = org._count?.clubMembership ?? 0;
  const createdYear = new Date(org.createdAt).getFullYear();
  const hasCoords = org.latitude != null && org.longitude != null;

  // OG image: logo si dispo
  const ogImage = logoFile ? getFileUrl(logoFile) : undefined;

  function scrollTo(sectionId: string) {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleTabClick(i: number) {
    setActiveTab(i);
    const ids = ['section-presentation', 'section-concours', 'section-passes', 'section-blog', 'section-contact'];
    scrollTo(ids[i]);
  }

  return (
    <div className='bg-[#F8FAFC] text-slate-900 font-sans'>
      <PageMeta
        title={org.name}
        description={
          org.description
            ? org.description.slice(0, 160)
            : `Découvrez les concours de pétanque organisés par ${org.name} sur BoulesConnect.`
        }
        image={ogImage}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: org.name,
          url: `${SITE_URL}/organisateurs/${org.id}`,
          ...(org.description ? { description: org.description.slice(0, 300) } : {}),
          ...(ogImage ? { logo: ogImage } : {}),
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Organisateurs', item: `${SITE_URL}/organisateurs` },
            { '@type': 'ListItem', position: 3, name: org.name, item: `${SITE_URL}/organisateurs/${org.id}` },
          ],
        }}
      />

      {/* Cover */}
      {coverFile ? (
        <div className='h-56 sm:h-64 md:h-80 relative overflow-hidden'>
          <img src={getFileUrl(coverFile)} alt='' className='w-full h-full object-cover object-top' />
        </div>
      ) : (
        <div className='h-56 sm:h-64 md:h-80 bg-gradient-to-br from-purple-900 via-indigo-600 to-blue-800 relative overflow-hidden'>
          <div className='absolute inset-0 pointer-events-none'>
            <div className='absolute top-10 right-20 w-40 h-40 rounded-full bg-white/10' />
            <div className='absolute bottom-10 left-10 w-32 h-32 rounded-full bg-white/5' />
            <div className='absolute top-20 left-1/3 w-24 h-24 rounded-full bg-white/10' />
          </div>
        </div>
      )}

      {/* Profile Card */}
      <section className='px-6'>
        <div className='max-w-7xl mx-auto -mt-24 relative z-10'>
          <div className='bg-white rounded-3xl border border-slate-200 shadow-xl p-8'>
            <div className='flex flex-col md:flex-row gap-8'>
              {/* Logo */}
              {logoFile ? (
                <img
                  src={getFileUrl(logoFile)}
                  alt={org.name}
                  className='w-32 h-32 rounded-3xl object-cover shadow-lg shrink-0 border border-slate-100'
                />
              ) : (
                <div
                  className={`w-32 h-32 bg-gradient-to-br ${logoColor} rounded-3xl flex items-center justify-center text-white font-extrabold text-4xl shadow-lg shrink-0`}
                >
                  {getNameInitials(org.name)}
                </div>
              )}

              {/* Info */}
              <div className='flex-1'>
                <h1 className='text-3xl md:text-4xl font-extrabold text-slate-900 mb-2'>{org.name}</h1>
                {org.description && <p className='text-slate-500 mb-4 line-clamp-2'>{org.description}</p>}

                <div className='flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-4'>
                  {(org.city ?? org.department) && (
                    <span className='flex items-center gap-1.5'>
                      <MapPin className='w-4 h-4' />
                      {[org.city, org.department].filter(Boolean).join(', ')}
                    </span>
                  )}
                  <span className='flex items-center gap-1.5'>
                    <Calendar className='w-4 h-4' /> Depuis {createdYear}
                  </span>
                  {totalCompetitions > 0 && (
                    <span className='flex items-center gap-1.5'>
                      <Trophy className='w-4 h-4' /> {totalCompetitions} concours organisés
                    </span>
                  )}
                </div>

                <div className='flex flex-wrap items-center gap-3'>
                  {totalCompetitions > 0 && (
                    <span className='inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-full text-xs font-bold'>
                      <Trophy className='w-3.5 h-3.5' /> {totalCompetitions}+ events
                    </span>
                  )}
                  {upcomingTotal > 0 && (
                    <span className='inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-full text-xs font-bold'>
                      <Calendar className='w-3.5 h-3.5' /> {upcomingTotal} à venir
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mt-8'>
              {[
                {
                  label: 'Concours organisés',
                  value: String(totalCompetitions),
                  icon: Trophy,
                  color: 'text-purple-600 bg-purple-50',
                },
                {
                  label: 'À venir',
                  value: String(upcomingTotal),
                  icon: Calendar,
                  color: 'text-blue-600 bg-blue-50',
                },
                {
                  label: 'Événements passés',
                  value: String(pastTotal),
                  icon: Award,
                  color: 'text-emerald-600 bg-emerald-50',
                },
                {
                  label: 'Clubs affiliés',
                  value: String(clubsCount),
                  icon: Building2,
                  color: 'text-slate-600 bg-slate-100',
                },
              ].map(stat => (
                <div key={stat.label} className='bg-slate-50 rounded-2xl p-5 text-center'>
                  <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mx-auto mb-2`}>
                    <stat.icon className='w-5 h-5' />
                  </div>
                  <div className='text-xl font-extrabold text-slate-900'>{stat.value}</div>
                  <div className='text-xs text-slate-500'>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Tab Nav */}
      <nav className='sticky top-16 z-30 bg-white/80 backdrop-blur-lg border-b border-slate-200 mt-8'>
        <div className='max-w-7xl mx-auto px-6'>
          <div className='flex items-center gap-1 overflow-x-auto py-3'>
            {TABS.map((tab, i) => (
              <button
                key={tab}
                onClick={() => handleTabClick(i)}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                  activeTab === i ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className='px-6 py-12'>
        <div className='max-w-7xl mx-auto flex flex-col lg:flex-row gap-8'>
          {/* Left (2/3) */}
          <div className='lg:w-2/3 space-y-8'>
            {/* À propos */}
            <div id='section-presentation' className='bg-white rounded-3xl border border-slate-200 p-8 scroll-mt-36'>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-4'>À propos</h2>
              {org.description ? (
                <p className='text-slate-600 leading-relaxed whitespace-pre-line'>{org.description}</p>
              ) : (
                <p className='text-slate-400 italic'>Cet organisateur n&apos;a pas encore renseigné de description.</p>
              )}
            </div>

            {/* Upcoming competitions */}
            <div id='section-concours' className='bg-white rounded-3xl border border-slate-200 p-8 scroll-mt-36'>
              <div className='flex items-center justify-between mb-6'>
                <h2 className='text-2xl font-extrabold text-slate-900'>Prochains concours</h2>
                <Link
                  to={`${ROUTES.SEARCH_CONCOURS}?organizer=${org.id}`}
                  className='text-sm font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1'
                >
                  Voir tous <ArrowRight className='w-4 h-4' />
                </Link>
              </div>
              {upcoming.length === 0 ? (
                <div className='py-10 text-center'>
                  <Trophy className='w-10 h-10 text-slate-200 mx-auto mb-3' />
                  <p className='text-slate-400 text-sm'>Aucun concours à venir pour le moment.</p>
                </div>
              ) : (
                <div className='space-y-4'>
                  {upcoming.map(comp => (
                    <UpcomingCard key={comp.id} comp={comp} />
                  ))}
                </div>
              )}
            </div>

            {/* Past events */}
            <div id='section-passes' className='bg-white rounded-3xl border border-slate-200 p-8 scroll-mt-36'>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-6'>Événements passés</h2>
              {past.length === 0 ? (
                <p className='text-slate-400 text-sm italic'>Aucun événement terminé enregistré.</p>
              ) : (
                <div className='grid md:grid-cols-2 gap-4'>
                  {past.map(comp => (
                    <PastCard key={comp.id} comp={comp} />
                  ))}
                </div>
              )}
            </div>

            {/* Blog */}
            <div id='section-blog' className='bg-white rounded-3xl border border-slate-200 p-8 scroll-mt-36'>
              <div className='flex items-center justify-between mb-6'>
                <h2 className='text-2xl font-extrabold text-slate-900'>Blog</h2>
                {articles.length > 0 && (
                  <Link
                    to={ROUTES.ORGANIZATION_BLOG.replace(':id', String(org.id))}
                    className='text-sm font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1'
                  >
                    Voir tous <ArrowRight className='w-4 h-4' />
                  </Link>
                )}
              </div>
              {articles.length === 0 ? (
                <div className='py-10 text-center'>
                  <FileText className='w-10 h-10 text-slate-200 mx-auto mb-3' />
                  <p className='text-slate-400 text-sm'>Aucun article publié pour le moment.</p>
                </div>
              ) : (
                <div className='grid md:grid-cols-2 gap-4'>
                  {articles.map(article => (
                    <Link
                      key={article.id}
                      to={ROUTES.ARTICLE.replace(':slug', article.slug)}
                      className='bg-slate-50 rounded-2xl p-5 hover:bg-slate-100 transition-colors group'
                    >
                      <h4 className='font-bold text-slate-900 group-hover:text-purple-600 transition-colors line-clamp-2'>
                        {article.title}
                      </h4>
                      {article.excerpt && <p className='text-sm text-slate-500 mt-1 line-clamp-2'>{article.excerpt}</p>}
                      <div className='flex items-center gap-3 text-xs text-slate-400 mt-3'>
                        <span>{formatDateAbbreviatedFr(article.publishedAt ?? article.createdAt)}</span>
                        {article.category && (
                          <span className='px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded-md'>
                            {article.category.name}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right sidebar (1/3) */}
          <div className='lg:w-1/3 space-y-6'>
            <div className='sticky top-36 space-y-6'>
              {/* Contact */}
              <div id='section-contact' className='bg-white rounded-3xl border border-slate-200 p-6 scroll-mt-36'>
                <h3 className='font-bold text-lg text-slate-900 mb-4'>Contact</h3>
                <div className='space-y-3'>
                  {org.phone && (
                    <div className='flex items-center gap-3 text-sm text-slate-600'>
                      <Phone className='w-5 h-5 text-purple-500 shrink-0' />
                      <a href={`tel:${org.phone}`} className='hover:text-purple-600 transition-colors'>
                        {org.phone}
                      </a>
                    </div>
                  )}
                  {/*{org.email && (*/}
                  {/*  <div className='flex items-center gap-3 text-sm text-slate-600'>*/}
                  {/*    <Mail className='w-5 h-5 text-purple-500 shrink-0' />*/}
                  {/*    <span className='truncate'>{org.email}</span>*/}
                  {/*  </div>*/}
                  {/*)}*/}
                  {org.website && (
                    <div className='flex items-center gap-3 text-sm text-slate-600'>
                      <Globe className='w-5 h-5 text-purple-500 shrink-0' />
                      <a
                        href={org.website}
                        target='_blank'
                        rel='noreferrer noopener'
                        className='hover:text-purple-600 transition-colors truncate'
                      >
                        {org.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                  {org.address && (
                    <div className='flex items-start gap-3 text-sm text-slate-600'>
                      <MapPin className='w-5 h-5 text-purple-500 shrink-0 mt-0.5' />
                      <span>
                        {org.address}
                        {org.postcode || org.city ? `, ${[org.postcode, org.city].filter(Boolean).join(' ')}` : ''}
                      </span>
                    </div>
                  )}

                  {!org.phone && !org.email && !org.website && !org.address && (
                    <p className='text-slate-400 text-sm italic'>Aucune information de contact renseignée.</p>
                  )}
                </div>

                {isAuthenticated && org.email && (
                  <button
                    onClick={() => setContactOpen(true)}
                    className='w-full mt-5 bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 text-sm'
                  >
                    <Mail className='w-4 h-4' /> Envoyer un message
                  </button>
                )}
              </div>

              {/* Map */}
              {hasCoords ? (
                <div className='bg-white rounded-3xl border border-slate-200 p-6'>
                  <h3 className='font-bold text-lg text-slate-900 mb-4'>Accès</h3>
                  <div className='h-48 rounded-2xl overflow-hidden border border-slate-100'>
                    <LeafletMap lat={org.latitude ?? undefined} lng={org.longitude ?? undefined} />
                  </div>
                  {(org.city ?? org.address) && (
                    <p className='text-xs text-slate-500 mt-2 text-center'>{org.address ?? org.city}</p>
                  )}
                </div>
              ) : (org.city ?? org.department) ? (
                <div className='bg-white rounded-3xl border border-slate-200 p-6'>
                  <h3 className='font-bold text-lg text-slate-900 mb-4'>Localisation</h3>
                  <div className='h-32 bg-slate-50 rounded-2xl flex flex-col items-center justify-center gap-2'>
                    <Map className='w-8 h-8 text-slate-300' />
                    <p className='text-sm text-slate-500'>{[org.city, org.department].filter(Boolean).join(', ')}</p>
                  </div>
                </div>
              ) : null}

              {/* Infos organisateur */}
              <div className='bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 rounded-3xl p-6 text-white'>
                <h3 className='font-bold text-lg mb-3'>Organisation</h3>
                <div className='space-y-2 text-sm text-purple-100'>
                  <div className='flex justify-between'>
                    <span>Membre depuis</span>
                    <span className='font-semibold text-white'>{createdYear}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Concours organisés</span>
                    <span className='font-semibold text-white'>{totalCompetitions}</span>
                  </div>
                </div>
              </div>

              {/* Demander à devenir admin */}
              {isAuthenticated && !isAlreadyOrgAdmin && (
                <div className='rounded-3xl border border-slate-200 bg-white p-6'>
                  <h3 className='mb-2 flex items-center gap-2 text-base font-bold text-slate-900'>
                    <UserPlus className='h-5 w-5 text-indigo-500' />
                    Rejoindre comme admin
                  </h3>
                  {adminRequested ? (
                    <p className='text-sm text-emerald-700'>
                      Demande envoyée ! Un responsable de l'organisation la traitera prochainement.
                    </p>
                  ) : (
                    <form
                      onSubmit={e => {
                        e.preventDefault();
                        adminRequestMutation.mutate();
                      }}
                      className='space-y-3'
                    >
                      <p className='text-sm text-slate-500'>
                        Vous souhaitez gérer les concours de cette organisation ?
                      </p>
                      <textarea
                        value={adminMessage}
                        onChange={e => setAdminMessage(e.target.value)}
                        placeholder='Présentez-vous brièvement (optionnel)'
                        maxLength={500}
                        rows={3}
                        className='w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none'
                      />
                      <button
                        type='submit'
                        disabled={adminRequestMutation.isPending}
                        className='w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-60'
                      >
                        {adminRequestMutation.isPending ? 'Envoi…' : 'Envoyer ma demande'}
                      </button>
                    </form>
                  )}
                </div>
              )}

              {/* Partager */}
              <ShareBloc
                title='Partager cette organisation'
                shareText={`${org.name}${org.city ? ` — ${org.city}` : ''} | BoulesConnect`}
              />
            </div>
          </div>
        </div>
      </section>

      {contactOpen && (
        <ContactModal
          recipientName={org.name}
          senderName={[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
          isPending={contactMutation.isPending}
          onSend={contactMutation.mutate}
          onClose={() => setContactOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────
export default function OrganizationProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orgId = id ? parseInt(id, 10) : NaN;

  const {
    data: org,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.organizations.detail(orgId),
    queryFn: () => getOrganizationApi(orgId),
    enabled: !isNaN(orgId),
  });

  const isPublicProfile = org?.isProfilePublic === true;

  const { data: upcomingData } = useQuery({
    queryKey: queryKeys.competitions.list({
      organizationId: orgId,
      upcoming: true,
      limit: 6,
      notStatus: CompetitionStatus.DRAFT,
    }),
    queryFn: () =>
      getCompetitionsApi({
        organizationId: orgId,
        upcoming: true,
        limit: 6,
        sortBy: 'date',
        sortOrder: 'asc',
        notStatus: CompetitionStatus.DRAFT,
      }),
    enabled: !isNaN(orgId) && isPublicProfile,
  });

  const { data: pastData } = useQuery({
    queryKey: queryKeys.competitions.list({
      organizationId: orgId,
      status: CompetitionStatus.FINISHED,
      limit: 4,
    }),
    queryFn: () =>
      getCompetitionsApi({
        organizationId: orgId,
        status: CompetitionStatus.FINISHED,
        limit: 4,
        sortBy: 'date',
        sortOrder: 'desc',
      }),
    enabled: !isNaN(orgId) && isPublicProfile,
  });

  const { data: articlesData } = useQuery({
    queryKey: queryKeys.blog.articles({ organizationId: orgId, limit: 4 }),
    queryFn: () => getPublishedArticlesApi({ organizationId: orgId, limit: 4 }),
    enabled: !isNaN(orgId) && isPublicProfile,
  });

  if (isNaN(orgId)) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-[#F8FAFC]'>
        <div className='text-center'>
          <p className='text-slate-500 mb-4'>Identifiant d&apos;organisation invalide.</p>
          <button
            onClick={() => navigate(ROUTES.SEARCH_ORGANIZATIONS)}
            className='text-sm font-semibold text-purple-600 hover:text-purple-700'
          >
            ← Retour à l&apos;annuaire
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) return <Skeleton />;

  if (isError || !org) {
    return (
      <div className='min-h-[60vh] flex items-center justify-center bg-[#F8FAFC]'>
        {/* URL dynamique non publiable (404 API) : noindex anti duplicate content. */}
        <PageMeta
          title='Organisation introuvable'
          description="Cette organisation n'existe pas ou n'est pas accessible publiquement."
          robots='noindex, nofollow'
        />
        <div className='text-center max-w-sm'>
          <Building2 className='w-12 h-12 text-slate-300 mx-auto mb-4' />
          <h2 className='text-xl font-bold text-slate-900 mb-2'>Organisation introuvable</h2>
          <p className='text-slate-500 text-sm mb-6'>
            Cette organisation n&apos;existe pas ou n&apos;est plus disponible.
          </p>
          <Link
            to={ROUTES.SEARCH_ORGANIZATIONS}
            className='inline-flex items-center gap-2 text-sm font-semibold text-purple-600 hover:text-purple-700'
          >
            <ArrowLeft className='w-4 h-4' /> Retour à l&apos;annuaire
          </Link>
        </div>
      </div>
    );
  }

  if (!org.isProfilePublic) {
    return (
      <div className='min-h-screen bg-[#F8FAFC] text-slate-900'>
        <PageMeta title={org.name} description='Ce profil organisateur est privé.' robots='noindex, nofollow' />

        <div className='relative'>
          <div className='h-56 sm:h-64 md:h-80 bg-gradient-to-br from-purple-900 via-indigo-600 to-blue-800 blur-sm opacity-60'>
            <div className='absolute inset-0 pointer-events-none'>
              <div className='absolute top-10 right-20 w-40 h-40 rounded-full bg-white/10' />
              <div className='absolute bottom-10 left-10 w-32 h-32 rounded-full bg-white/5' />
            </div>
          </div>
        </div>

        <section className='px-6'>
          <div className='max-w-7xl mx-auto -mt-24 relative z-10'>
            <div className='bg-white rounded-3xl border border-slate-200 shadow-xl p-8'>
              <div className='flex flex-col md:flex-row gap-8'>
                <div className='w-32 h-32 bg-gradient-to-br from-purple-700 to-indigo-600 rounded-3xl flex items-center justify-center text-white font-extrabold text-4xl shadow-lg shrink-0 blur-sm opacity-60'>
                  {getNameInitials(org.name)}
                </div>
                <div className='flex-1'>
                  <h1 className='text-3xl md:text-4xl font-extrabold text-slate-900 mb-3'>{org.name}</h1>
                  {(org.city ?? org.department) && (
                    <div className='flex items-center gap-1.5 text-sm text-slate-500'>
                      <MapPin className='w-3.5 h-3.5' />
                      {[org.city, org.department].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className='px-6 py-16'>
          <div className='max-w-2xl mx-auto'>
            <div className='bg-white rounded-3xl border border-slate-200 shadow-sm p-12 text-center'>
              <div className='w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6'>
                <EyeOff className='w-8 h-8 text-slate-400' />
              </div>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-3'>Profil privé</h2>
              <p className='text-slate-500 leading-relaxed mb-8'>
                Cet organisateur a choisi de rendre son profil privé. Ses informations détaillées et ses concours ne
                sont pas accessibles publiquement.
              </p>
              <Link
                to={ROUTES.SEARCH_ORGANIZATIONS}
                className='inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-purple-700 transition-colors'
              >
                Retour à l&apos;annuaire
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <ProfileContent
      org={org}
      upcoming={upcomingData?.data ?? []}
      upcomingTotal={upcomingData?.meta?.total ?? 0}
      past={pastData?.data ?? []}
      pastTotal={pastData?.meta?.total ?? 0}
      articles={articlesData?.data ?? []}
    />
  );
}
